import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

function RecordList({ records, emptyText }) {
  if (!records.length) {
    return <p className="text-sm text-gray-400">{emptyText}</p>;
  }

  return (
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {records.map((record, index) => (
        <label key={`${record.hw_name || record.test_name}-${index}`} className="flex items-center gap-2 text-sm text-gray-800">
          <input type="checkbox" checked={record.handed_in} readOnly className="h-4 w-4" />
          <span>{record.hw_name || record.test_name}</span>
        </label>
      ))}
    </div>
  );
}

export default function StudentFollowUp({ studentId }) {
  const [data, setData] = useState(null);
  const [courseId, setCourseId] = useState("");
  const requestSeq = useRef(0);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  const load = async (nextCourseId) => {
    if (!studentId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError("");
    if (nextCourseId) {
      setData((prev) =>
        prev
          ? { ...prev, progress: null, hw_to_check: [], test_records: [], hw_records: [] }
          : prev
      );
    }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/studentFollowUp/${studentId}`,
        { params: nextCourseId ? { courseId: nextCourseId } : {} }
      );
      if (seq !== requestSeq.current) return;
      if (!res.data.success) {
        throw new Error(res.data.error || "Failed to load follow up.");
      }
      setData(res.data.data);
      setCourseId(String(res.data.data.selected_course_id || nextCourseId || ""));
      setRemarks(res.data.data.remarks || "");
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error("Failed to load student follow up:", err);
      setError(err.response?.data?.error || err.message || "Failed to load follow up.");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
    setCourseId("");
    setRemarks("");
    load("");
  }, [studentId]);

  const saveRemarks = async () => {
    setSaving(true);
    setSaveMessage("");
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/studentFollowUp/${studentId}/remarks`,
        { remarks }
      );
      if (!res.data.success) throw new Error(res.data.error || "Failed to save remarks.");
      setSaveMessage("Remarks saved.");
    } catch (err) {
      setSaveMessage(err.response?.data?.error || "Failed to save remarks.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return <p className="text-center text-gray-500 py-10">Loading follow up...</p>;
  }
  if (error && !data) {
    return <p className="text-center text-red-600 py-10">{error}</p>;
  }
  if (!data) return null;

  return (
    <div className="space-y-5">
      {data.new_topic_reminders?.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <p className="text-sm font-semibold text-amber-900">New topic to follow up</p>
          {data.new_topic_reminders.map((item) => (
            <p key={`${item.course_name}-${item.lesson_no}`} className="mt-1 text-sm text-amber-800">
              {item.message}
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">student_string</p>
          <p className="mt-1 text-sm font-medium text-gray-900">{data.student_string}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">video course</label>
          <select
            value={courseId}
            onChange={(e) => {
              const nextId = e.target.value;
              setCourseId(nextId);
              load(nextId);
            }}
            className="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            {data.courses.length === 0 && <option value="">No video course</option>}
            {data.courses.map((item) => (
              <option key={item.course_id || item.course_name} value={item.course_id}>
                {item.course_name}
                {item.course_name === data.current_video_course ? " (current)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">progress</p>
          <p className="mt-1 text-sm font-medium text-gray-900">{data.progress || "N/A"}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">HW to check</p>
          {data.hw_to_check?.length > 0 ? (
            <ul key={`check-${courseId}`} className="mt-2 space-y-1 text-sm text-gray-800">
              {data.hw_to_check.map((item, index) => (
                <li key={`${courseId}-${item.hw_name}-${index}`}>
                  {item.hw_name}
                  <span className="text-gray-500"> · due lesson {item.hw_due_lesson}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-sm text-gray-400">No homework due for this course today.</p>
          )}
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Test records</p>
          <RecordList key={`tests-${courseId}`} records={data.test_records || []} emptyText="No test records." />
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">HW records</p>
          <RecordList key={`hw-${courseId}`} records={data.hw_records || []} emptyText="No homework records." />
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">New topic to follow up</p>
        <p className="mt-1 text-sm text-gray-700">
          {data.new_topic_reminders?.length > 0
            ? "today is the second last lesson."
            : "-"}
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500" htmlFor="followUpRemarks">
          remarks
        </label>
        <textarea
          id="followUpRemarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={4}
          className="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder="Write a follow-up note for this student"
        />
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={saveRemarks}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save remarks"}
          </button>
          {saveMessage && <span className="text-sm text-gray-600">{saveMessage}</span>}
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
